import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { ShoppingCart, Plus, Minus, Trash2, LogOut, Search, Package, ShoppingBag, Phone, Check, User, Clock, CircleCheck, FileText, Download, UserPlus, LogIn } from "lucide-react";

// Axios instance SEM auth header (loja é pública)
const lojaAxios = axios.create();
lojaAxios.interceptors.request.use((config) => {
  // Garante que não vai header de Bearer do admin
  if (config.headers) delete config.headers.Authorization;
  return config;
});

const API = `${window.location.origin}/api`;
const STORAGE_KEY = "loja_virtual_cliente";

const NIVEIS = {
  bronze:   { nome: "Bronze",   emoji: "🥉", cor: "from-amber-600 to-orange-700" },
  prata:    { nome: "Prata",    emoji: "🥈", cor: "from-slate-300 to-slate-500" },
  ouro:     { nome: "Ouro",     emoji: "🥇", cor: "from-yellow-300 to-yellow-500" },
  platinum: { nome: "Platinum", emoji: "⭐", cor: "from-cyan-200 to-cyan-400" },
  diamante: { nome: "Diamante", emoji: "💎", cor: "from-sky-300 to-blue-500" },
};

const formatMoeda = (v) => `R$ ${(Number(v) || 0).toFixed(2).replace(".", ",")}`;

export default function LojaVirtualPage() {
  const [cliente, setCliente] = useState(null);  // { id, nome, limite_compra, nivel, cashback_saldo }
  const [catalogo, setCatalogo] = useState(null); // { cliente, carro_nome, produtos[], kits[], banners, categorias }
  const [cart, setCart] = useState({});            // key = "prod:<id>" ou "kit:<id>" -> qtd
  const [itemsCache, setItemsCache] = useState({}); // key -> item (acumula entre filtros de categoria)
  const [categoriaFiltro, setCategoriaFiltro] = useState("");
  const [loading, setLoading] = useState(false);
  const [telefone, setTelefone] = useState("");
  const [busca, setBusca] = useState("");
  const [showCart, setShowCart] = useState(false);
  const [observacoes, setObservacoes] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [confirmacao, setConfirmacao] = useState(null); // { numero_pedido, valor_total }
  const [usarCashback, setUsarCashback] = useState(false);
  const [showPerfil, setShowPerfil] = useState(false);
  const [perfilData, setPerfilData] = useState(null);
  const [loadingPerfil, setLoadingPerfil] = useState(false);
  const [installPromptEvent, setInstallPromptEvent] = useState(null);
  const [mostrarInstalar, setMostrarInstalar] = useState(false);
  const [showIOSInstall, setShowIOSInstall] = useState(false);
  // Quando o cliente abre /loja sem user_id na URL, mostra tela "sem loja" amigável
  const [lojaNaoSelecionada, setLojaNaoSelecionada] = useState(false);
  // Modal central de Login/Cadastro (aparece ao finalizar pedido sem login)
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authTab, setAuthTab] = useState("login"); // 'login' | 'cadastro'
  const [cadastroForm, setCadastroForm] = useState({
    nome_completo: "",
    telefone: "",
    cpf_rg: "",
    endereco: "",
    cidade: "",
    data_nascimento: "",
    instagram: "",
  });
  const [cadastroLoading, setCadastroLoading] = useState(false);
  const [cadastroEnviado, setCadastroEnviado] = useState(false);
  // Pagar Promissória via InfinitePay
  const [pagarPromissoria, setPagarPromissoria] = useState(null);
  const [pagarValor, setPagarValor] = useState("");
  const [pagarLoading, setPagarLoading] = useState(false);

  const isIOS = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isStandalone = typeof window !== "undefined" && (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true);

  // Registra o service worker PWA apenas na loja virtual
  // Swap dinâmico do manifest e do tema quando o cliente entra na Loja.
  // Sem isso, o navegador instala o PWA usando o manifest padrão (Compromisso Digital).
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    const themeMeta = document.querySelector('meta[name="theme-color"]');
    const titleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    const prevManifest = link?.getAttribute("href");
    const prevTheme = themeMeta?.getAttribute("content");
    const prevTitle = titleMeta?.getAttribute("content");
    if (link) link.setAttribute("href", "/manifest-loja.json");
    if (themeMeta) themeMeta.setAttribute("content", "#EC4899");
    if (titleMeta) titleMeta.setAttribute("content", "Loja Online");
    return () => {
      // Restaura o manifest principal quando o cliente sai da loja
      if (link && prevManifest) link.setAttribute("href", prevManifest);
      if (themeMeta && prevTheme) themeMeta.setAttribute("content", prevTheme);
      if (titleMeta && prevTitle) titleMeta.setAttribute("content", prevTitle);
    };
  }, []);

  useEffect(() => {
    if ("serviceWorker" in navigator && window.location.protocol === "https:") {
      navigator.serviceWorker.register("/sw.js", { scope: "/loja" }).catch(() => {});
    }
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPromptEvent(e);
      // Só mostrar se ainda não instalado e o cliente já passou do login
      if (!window.matchMedia("(display-mode: standalone)").matches) {
        setMostrarInstalar(true);
      }
    };
    const onInstalled = () => {
      setMostrarInstalar(false);
      setInstallPromptEvent(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const instalarApp = async () => {
    // Já instalado
    if (isStandalone) {
      toast.success("O app já está instalado!");
      return;
    }
    // iOS: sem API nativa — abre modal com instruções visuais
    if (isIOS) {
      setShowIOSInstall(true);
      return;
    }
    // Chrome/Android/Desktop: dispara o prompt nativo direto
    if (installPromptEvent) {
      installPromptEvent.prompt();
      const choice = await installPromptEvent.userChoice;
      if (choice?.outcome === "accepted") {
        toast.success("App instalado! Acesse pela tela de início.");
      }
      setInstallPromptEvent(null);
      setMostrarInstalar(false);
      return;
    }
    // Fallback: navegador ainda não disparou o evento (ou não compatível)
    toast.info(
      "Para instalar: toque no menu do navegador (⋮) e escolha 'Instalar app' ou 'Adicionar à tela de início'.",
      { duration: 6000 }
    );
  };

  // ====== Push Notifications ======
  const urlB64ToUint8 = (b64) => {
    const padding = "=".repeat((4 - (b64.length % 4)) % 4);
    const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = window.atob(base64);
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
    return out;
  };

  const assinarPush = async (cli) => {
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
      if (Notification.permission === "denied") return;
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        const perm = Notification.permission === "granted" ? "granted" : await Notification.requestPermission();
        if (perm !== "granted") return;
        const { data: keyResp } = await lojaAxios.get(`${API}/loja/push/public-key`);
        if (!keyResp?.public_key) return;
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlB64ToUint8(keyResp.public_key),
        });
      }
      await lojaAxios.post(`${API}/loja/push/subscribe`, {
        cliente_id: cli.id,
        telefone: cli.telefone,
        subscription: sub.toJSON(),
      });
    } catch (e) {
      // silencioso — push é opcional
    }
  };

  const loadPerfil = async () => {
    if (!cliente) return;
    setLoadingPerfil(true);
    try {
      const { data } = await lojaAxios.post(`${API}/loja/meus-pedidos`, { cliente_id: cliente.id, telefone: cliente.telefone });
      setPerfilData(data);
    } catch (err) {
      toast.error("Erro ao carregar perfil");
    } finally {
      setLoadingPerfil(false);
    }
  };

  const abrirPerfil = () => {
    setShowPerfil(true);
    loadPerfil();
  };

  // Carrega resumo do perfil automaticamente ao logar/abrir a loja
  useEffect(() => {
    if (cliente) {
      loadPerfil();
      // Marca notificações como lidas e limpa app badge ao abrir a loja
      marcarNotificacoesLidas(cliente);
      const iv = setInterval(loadPerfil, 60000);
      // Se voltou de pagamento InfinitePay, abrir "Meus Pedidos" automaticamente
      try {
        const sp = new URLSearchParams(window.location.search);
        if (sp.get("pago") === "1") {
          setShowPerfil(true);
          toast.success("Pagamento processado — confira seus pedidos!");
          // Remove o query param da URL para não reabrir em refresh
          const url = new URL(window.location.href);
          url.searchParams.delete("pago");
          window.history.replaceState({}, "", url.toString());
        }
      } catch (_) { /* noop */ }
      return () => clearInterval(iv);
    }
  }, [cliente?.id]);

  // Marca notificações como lidas no servidor + limpa o badge do app via SW
  const marcarNotificacoesLidas = async (c) => {
    try {
      await lojaAxios.post(`${API}/loja/notificacoes/marcar-lidas`, {
        cliente_id: c.id,
        telefone: c.telefone,
      });
    } catch {}
    try {
      if ("serviceWorker" in navigator) {
        const reg = await navigator.serviceWorker.ready;
        if (reg.active) reg.active.postMessage({ type: "clear-badge" });
      }
      if ("clearAppBadge" in navigator) {
        navigator.clearAppBadge().catch(() => {});
      }
    } catch {}
  };

  // Atualiza dinamicamente o manifest.json do PWA para refletir nome/logo do lojista.
  // Roda toda vez que o catálogo carregar — mesmo SEM logo, o nome do app no celular
  // já será o nome da loja do tenant (não o do super admin).
  useEffect(() => {
    if (!catalogo) return;
    try {
      const uid = catalogo?.user_id
        || catalogo?.cliente?.user_id
        || (new URLSearchParams(window.location.search).get("uid") || new URLSearchParams(window.location.search).get("user_id") || "");
      if (!uid) return;
      const manifestUrl = `${API}/loja/manifest?user_id=${uid}`;
      let link = document.querySelector('link[rel="manifest"]');
      if (!link) {
        link = document.createElement("link");
        link.rel = "manifest";
        document.head.appendChild(link);
      }
      link.href = manifestUrl;
      // Apple touch icon dinâmico (se houver logo)
      if (catalogo?.perfil_loja?.logo_path) {
        const fn = String(catalogo.perfil_loja.logo_path).split("/").pop();
        const logoUrl = `${API}/loja/logo-file/${fn}`;
        let apple = document.querySelector('link[rel="apple-touch-icon"]');
        if (!apple) { apple = document.createElement("link"); apple.rel = "apple-touch-icon"; document.head.appendChild(apple); }
        apple.href = logoUrl;
        let fav = document.querySelector('link[rel="icon"]');
        if (!fav) { fav = document.createElement("link"); fav.rel = "icon"; document.head.appendChild(fav); }
        fav.href = logoUrl;
      }
      // Theme color
      const primary = catalogo?.tema?.primary;
      if (primary) {
        let theme = document.querySelector('meta[name="theme-color"]');
        if (!theme) { theme = document.createElement("meta"); theme.name = "theme-color"; document.head.appendChild(theme); }
        theme.content = primary;
      }
      // Title da página = nome da loja
      const nomeLoja = catalogo?.perfil_loja?.nome_loja;
      if (nomeLoja) document.title = nomeLoja;
    } catch (_) { /* noop */ }
  }, [catalogo?.user_id, catalogo?.perfil_loja?.logo_path, catalogo?.perfil_loja?.nome_loja, catalogo?.tema?.primary]);

  // Bootstrap: lê do localStorage; se não houver cliente, carrega catálogo público
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const c = JSON.parse(saved);
        if (c?.id && c?.telefone) {
          setCliente(c);
          loadCatalogo(c);
          setTimeout(() => assinarPush(c), 3000);
          return;
        }
      } catch {}
    }
    // Sem login: carrega catálogo público (Cliente Final)
    loadCatalogoPublico();
  }, []);

  const loadCatalogoPublico = async (catFiltro = null) => {
    setLoading(true);
    try {
      const params = {};
      if (catFiltro) params.categoria = catFiltro;
      // Suporte a multi-tenant: lê ?uid= ou ?user_id= da URL para abrir a loja
      // de um assinante específico (não a do super admin).
      const sp = new URLSearchParams(window.location.search);
      const uidParam = sp.get("uid") || sp.get("user_id");
      if (uidParam) params.user_id = uidParam;
      const { data } = await lojaAxios.get(`${API}/loja/catalogo-publico`, { params });
      setCatalogo(data);
      setLojaNaoSelecionada(false);
      setItemsCache(prev => {
        const next = { ...prev };
        (data.produtos || []).forEach(p => { next[`prod:${p.id}`] = p; });
        (data.kits || []).forEach(k => { next[`kit:${k.id}`] = k; });
        return next;
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Sem user_id na URL → mostra a tela "selecione sua loja"
      if (err?.response?.status === 404 && detail === "LOJA_REQUER_LINK") {
        setLojaNaoSelecionada(true);
      } else if (err?.response?.status === 404) {
        toast.error("Loja não encontrada. Confirme o link recebido do seu vendedor.");
        setLojaNaoSelecionada(true);
      } else {
        toast.error(detail || "Erro ao carregar catálogo público");
      }
    } finally {
      setLoading(false);
    }
  };

  const loadCatalogo = async (c, catFiltro = null) => {
    setLoading(true);
    try {
      const params = { cliente_id: c.id, telefone: c.telefone };
      if (catFiltro) params.categoria = catFiltro;
      const { data } = await lojaAxios.get(`${API}/loja/catalogo`, { params });
      setCatalogo(data);
      // Acumula produtos/kits no cache para que o carrinho mantenha preços ao trocar de categoria
      setItemsCache(prev => {
        const next = { ...prev };
        (data.produtos || []).forEach(p => { next[`prod:${p.id}`] = p; });
        (data.kits || []).forEach(k => { next[`kit:${k.id}`] = k; });
        return next;
      });
      // Atualiza saldo/nivel do cliente no localStorage se mudou
      if (data.cliente) {
        const atualizado = { ...c, nivel: data.cliente.nivel, cashback_saldo: data.cliente.cashback_saldo, limite_compra: data.cliente.limite_compra };
        setCliente(atualizado);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(atualizado));
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erro ao carregar catálogo";
      toast.error(msg);
      if (err?.response?.status === 401) handleLogout();
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e?.preventDefault?.();
    if (!telefone.replace(/\D/g, "")) return;
    setLoading(true);
    try {
      const { data } = await lojaAxios.post(`${API}/loja/login`, { telefone });
      const c = { id: data.id, nome: data.nome_completo, telefone: data.telefone, limite_compra: data.limite_compra };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(c));
      setCliente(c);
      toast.success(`Bem-vindo(a), ${data.nome_completo}!`);
      setShowAuthModal(false);
      loadCatalogo(c);
      setTimeout(() => assinarPush(c), 2000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao entrar");
    } finally {
      setLoading(false);
    }
  };

  const handleCadastro = async (e) => {
    e?.preventDefault?.();
    const nome = (cadastroForm.nome_completo || "").trim();
    const tel = (cadastroForm.telefone || "").trim();
    if (!nome || !tel.replace(/\D/g, "")) {
      toast.error("Informe seu nome e telefone");
      return;
    }
    setCadastroLoading(true);
    try {
      const { data } = await lojaAxios.post(`${API}/loja/cadastro-cliente`, cadastroForm);
      if (data?.ja_existia) {
        toast.info("Você já tem cadastro pendente. Aguarde a aprovação.");
      } else {
        toast.success("Cadastro enviado!");
      }
      setCadastroEnviado(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar cadastro");
    } finally {
      setCadastroLoading(false);
    }
  };

  const resetCadastroForm = () => {
    setCadastroForm({
      nome_completo: "",
      telefone: "",
      cpf_rg: "",
      endereco: "",
      cidade: "",
      data_nascimento: "",
      instagram: "",
    });
    setCadastroEnviado(false);
  };

  // ====== Pagar via InfinitePay ======
  // URL do checkout pronta para fallback manual (caso o redirect automático falhe no iOS)
  const [checkoutUrlFallback, setCheckoutUrlFallback] = useState("");

  const pagamentoDisponivel = catalogo?.pagamento_disponivel !== false;

  const abrirPagarModal = (p) => {
    if (!pagamentoDisponivel) {
      toast.error("Pagamentos indisponíveis no momento. Procure a loja para regularizar.");
      return;
    }
    setPagarPromissoria(p);
    setPagarValor(Number(p.valor_restante || 0).toFixed(2));
    setCheckoutUrlFallback("");
  };

  const fecharPagarModal = () => {
    setPagarPromissoria(null);
    setPagarValor("");
  };

  const confirmarPagar = async () => {
    if (!pagarPromissoria || !cliente) return;
    const valor = parseFloat((pagarValor || "0").replace(",", "."));
    if (!valor || valor <= 0) { toast.error("Informe um valor válido"); return; }
    if (valor > Number(pagarPromissoria.valor_restante) + 0.01) {
      toast.error(`Valor máximo: ${formatMoeda(pagarPromissoria.valor_restante)}`);
      return;
    }
    setPagarLoading(true);
    try {
      const { data } = await lojaAxios.post(`${API}/loja/infinitepay/checkout`, {
        cliente_id: cliente.id,
        telefone: cliente.telefone,
        promissoria_id: pagarPromissoria.id,
        valor,
        redirect_url: `${window.location.origin}/loja?pago=1`,
      });
      const checkoutUrl = data?.checkout_url;
      if (!checkoutUrl) {
        toast.error("Não foi possível gerar o link. Tente novamente.");
        setPagarLoading(false);
        return;
      }
      // Guarda URL como fallback (botão manual no modal) — iOS Safari às vezes
      // bloqueia o redirect quando o user-gesture já se perdeu na chamada async.
      setCheckoutUrlFallback(checkoutUrl);
      // Redirecionamento DIRETO (sem setTimeout — iOS exige imediato após o async)
      try {
        window.location.assign(checkoutUrl);
      } catch (_) {
        window.location.href = checkoutUrl;
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao iniciar pagamento");
      setPagarLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setCliente(null);
    setCatalogo(null);
    setCart({});
    setItemsCache({});
    setShowCart(false);
  };

  // cart key = "prod:<id>" ou "kit:<id>"
  const keyOf = (item) => item.__isKit ? `kit:${item.id}` : `prod:${item.id}`;
  const estoqueOf = (item) => Number(item.estoque || 0);

  const addCart = (item) => {
    const k = keyOf(item);
    setCart(prev => {
      const atual = prev[k] || 0;
      if (atual + 1 > estoqueOf(item)) { toast.warning("Sem estoque suficiente"); return prev; }
      return { ...prev, [k]: atual + 1 };
    });
  };
  const incCartByKey = (k) => {
    const item = findItemByKey(k);
    if (!item) return;
    setCart(prev => {
      const atual = prev[k] || 0;
      const estoque = Number(item.estoque || 0);
      if (atual + 1 > estoque) { toast.warning("Sem estoque suficiente"); return prev; }
      return { ...prev, [k]: atual + 1 };
    });
  };
  const subCart = (k) => {
    setCart(prev => {
      const atual = prev[k] || 0;
      const next = { ...prev };
      if (atual <= 1) delete next[k];
      else next[k] = atual - 1;
      return next;
    });
  };
  const removerCart = (k) => {
    setCart(prev => { const n = { ...prev }; delete n[k]; return n; });
  };

  const findItemByKey = (k) => {
    // 1) procura no catálogo atual (categoria selecionada)
    if (catalogo) {
      if (k.startsWith("kit:")) {
        const kit = (catalogo.kits || []).find(x => x.id === k.slice(4));
        if (kit) return kit;
      } else {
        const p = (catalogo.produtos || []).find(x => x.id === k.slice(5));
        if (p) return p;
      }
    }
    // 2) fallback: cache acumulado de outras categorias já visitadas
    return itemsCache[k] || null;
  };

  const totalCart = useMemo(() => {
    return Object.entries(cart).reduce((sum, [k, qtd]) => {
      // Olha primeiro no catálogo atual, depois no cache acumulado
      let item = null;
      if (catalogo) {
        item = k.startsWith("kit:")
          ? (catalogo.kits || []).find(x => x.id === k.slice(4))
          : (catalogo.produtos || []).find(x => x.id === k.slice(5));
      }
      if (!item) item = itemsCache[k];
      const preco = k.startsWith("kit:")
        ? Number(item?.preco || 0)
        : Number(item?.preco_venda || item?.preco || 0);
      return sum + preco * qtd;
    }, 0);
  }, [cart, catalogo, itemsCache]);

  const countCart = useMemo(() => Object.values(cart).reduce((a, b) => a + b, 0), [cart]);

  // Percentual de cashback do nível do cliente (do catálogo)
  const pctCashbackNivel = useMemo(() => {
    const map = catalogo?.cashback_por_nivel || {};
    return Number(map[cliente?.nivel || 'bronze'] || 0);
  }, [catalogo, cliente]);

  const cashbackPrevisto = useMemo(() => {
    if (!pctCashbackNivel || totalCart <= 0) return 0;
    return Math.round((totalCart * pctCashbackNivel) / 100 * 100) / 100;
  }, [pctCashbackNivel, totalCart]);

  const saldoCashback = Number(cliente?.cashback_saldo || 0);
  const cashbackAplicado = usarCashback ? Math.min(saldoCashback, totalCart) : 0;
  const totalAPagar = Math.max(0, totalCart - cashbackAplicado);

  const produtosFiltrados = useMemo(() => {
    if (!catalogo) return [];
    const lista = [
      ...(catalogo.kits || []).map(k => ({ ...k, __isKit: true })),
      ...(catalogo.produtos || []).map(p => ({ ...p, __isKit: false }))
    ];
    if (!busca) return lista;
    const q = busca.toLowerCase();
    return lista.filter(p =>
      (p.nome || "").toLowerCase().includes(q) ||
      (p.codigo_sku || "").toLowerCase().includes(q)
    );
  }, [busca, catalogo]);

  const finalizar = async () => {
    if (countCart === 0) return;
    // Não logado → abre modal de Login/Cadastro
    if (!cliente) {
      setShowCart(false);
      setAuthTab("login");
      setShowAuthModal(true);
      return;
    }
    const limite = Number(cliente?.limite_compra || 0);
    if (limite > 0 && totalCart > limite) {
      toast.error(`Valor total ${formatMoeda(totalCart)} excede seu limite de ${formatMoeda(limite)}`);
      return;
    }
    setEnviando(true);
    try {
      const itens = Object.entries(cart).map(([k, qtd]) => {
        if (k.startsWith("kit:")) return { tipo: "kit", kit_id: k.slice(4), quantidade: qtd };
        return { produto_id: k.slice(5), quantidade: qtd };
      });
      const { data } = await lojaAxios.post(`${API}/loja/pedido`, {
        cliente_id: cliente.id,
        telefone: cliente.telefone,
        itens,
        observacoes,
        usar_cashback: usarCashback
      });
      setConfirmacao({ numero_pedido: data.numero_pedido, valor_total: data.valor_total, cashback_usado: data.cashback_usado || 0, aguardando_nota_aberta: data.aguardando_nota_aberta });
      setCart({});
      setObservacoes("");
      setUsarCashback(false);
      setShowCart(false);
      // Recarrega catalogo para atualizar saldo cashback
      loadCatalogo(cliente, categoriaFiltro);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar pedido");
    } finally {
      setEnviando(false);
    }
  };

  // ====== CONFIRMAÇÃO ======
  if (confirmacao) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-sky-50 flex items-center justify-center p-6">
        <Card className="max-w-md w-full border-2 border-emerald-300">
          <CardContent className="p-8 text-center space-y-4">
            <div className="w-20 h-20 mx-auto rounded-full bg-emerald-500 text-white flex items-center justify-center">
              <Check className="h-10 w-10" />
            </div>
            <h2 className="text-2xl font-bold text-emerald-800">Pedido Enviado!</h2>
            {confirmacao.aguardando_nota_aberta ? (
              <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-3 text-left">
                <p className="text-sm font-bold text-amber-900">⚠️ Atenção</p>
                <p className="text-xs text-amber-800 mt-1">
                  Seu pedido <strong>#{confirmacao.numero_pedido}</strong> está sendo <strong>separado</strong> e será <strong>liberado após o fechamento da nota em aberto</strong>.
                </p>
              </div>
            ) : (
              <p className="text-sm text-gray-600">Seu pedido <strong>#{confirmacao.numero_pedido}</strong> foi registrado com sucesso e está aguardando aprovação do vendedor.</p>
            )}
            <div className="bg-emerald-50 rounded-lg p-4">
              <p className="text-xs text-emerald-600 font-medium">Total do pedido</p>
              <p className="text-3xl font-bold text-emerald-700">{formatMoeda(confirmacao.valor_total)}</p>
              {confirmacao.cashback_usado > 0 && (
                <p className="text-xs text-amber-700 mt-2">💰 Cashback aplicado: <strong>{formatMoeda(confirmacao.cashback_usado)}</strong></p>
              )}
            </div>
            <p className="text-xs text-gray-500">Em breve seu vendedor entrará em contato para confirmar a entrega.</p>
            <Button onClick={() => setConfirmacao(null)} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="btn-voltar-catalogo">
              Voltar ao catálogo
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ====== LOJA SEM TENANT (URL sem ?uid=) ======
  if (lojaNaoSelecionada && !cliente) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-rose-50 flex items-center justify-center p-6" data-testid="loja-sem-link">
        <Card className="max-w-md w-full border-2 border-amber-300">
          <CardContent className="p-8 text-center space-y-4">
            <div className="w-20 h-20 mx-auto rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-4xl">
              🛍️
            </div>
            <h2 className="text-2xl font-bold text-gray-800">Loja não encontrada</h2>
            <p className="text-sm text-gray-600">
              Para acessar uma loja você precisa abrir o link enviado pelo seu vendedor.
            </p>
            <p className="text-xs text-gray-500">
              Se você já tem um cadastro, faça login abaixo:
            </p>
            <Button
              onClick={() => { setAuthTab("login"); setShowAuthModal(true); }}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              data-testid="btn-fazer-login-sem-tenant"
            >
              Já tenho cadastro — entrar
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ====== CATÁLOGO (público OU autenticado) ======
  // (Antes existia um early-return de login. Agora a loja é pública por padrão.)
  const corBtnComprar = catalogo?.tema?.cor_botao_comprar;
  const btnComprarStyle = corBtnComprar ? { backgroundColor: corBtnComprar } : undefined;
  const btnComprarClass = corBtnComprar ? "" : "bg-emerald-600 hover:bg-emerald-700";
  return (
    <div className="min-h-screen bg-gray-50 pb-28">
      {/* Header */}
      <header className="text-white px-4 py-3 sticky top-0 z-30 shadow-md" style={{ background: catalogo?.tema?.primary ? `linear-gradient(to right, ${catalogo.tema.primary}, ${catalogo.tema.secondary || catalogo.tema.primary})` : undefined }}>
        <div className={`max-w-5xl mx-auto flex items-center justify-between gap-3 ${!catalogo?.tema?.primary ? 'bg-gradient-to-r from-pink-400 to-rose-400 -mx-4 -my-3 px-4 py-3' : ''}`}>
          {/* Logo da loja (se configurada) */}
          {catalogo?.perfil_loja?.logo_path && (() => {
            const fn = String(catalogo.perfil_loja.logo_path).split('/').pop();
            return (
              <img
                src={`${API}/loja/logo-file/${fn}`}
                alt={catalogo.perfil_loja.nome_loja || 'Loja'}
                className="w-11 h-11 rounded-xl object-contain bg-white shadow-md shrink-0"
                data-testid="loja-virtual-logo"
              />
            );
          })()}
          <div className="flex-1 min-w-0">
            <p className="text-[10px] opacity-80 uppercase tracking-wide truncate">
              {catalogo?.perfil_loja?.nome_loja || "Loja Virtual"}
              {cliente && catalogo?.carro_nome ? ` · ${catalogo.carro_nome}` : ""}
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              {cliente ? (
                <p className="text-sm font-bold truncate">Olá, {cliente.nome?.split(" ")[0] || "Cliente"}</p>
              ) : (
                <p className="text-sm font-bold truncate">Bem-vinda! Explore o catálogo</p>
              )}
              {cliente && cliente.nivel && NIVEIS[cliente.nivel] && (
                <span className={`inline-flex items-center gap-1 text-[10px] font-bold rounded-full px-2 py-0.5 bg-gradient-to-r ${NIVEIS[cliente.nivel].cor} text-white shadow-sm`} data-testid="badge-nivel" title={`Nível ${NIVEIS[cliente.nivel].nome}`}>
                  <span className="text-sm">{NIVEIS[cliente.nivel].emoji}</span>
                  {NIVEIS[cliente.nivel].nome}
                </span>
              )}
              {cliente && Number(cliente.cashback_saldo) > 0 && (
                <span className="text-[10px] font-bold bg-amber-400 text-slate-900 rounded-full px-2 py-0.5" data-testid="cashback-saldo" title="Seu saldo de cashback">
                  💰 {formatMoeda(cliente.cashback_saldo)}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={() => setShowCart(true)}
            className="relative flex items-center gap-1 bg-white/10 hover:bg-white/20 rounded-full px-3 py-1.5 text-xs font-medium transition"
            data-testid="btn-abrir-carrinho"
          >
            <ShoppingCart className="h-4 w-4" />
            {countCart > 0 && (
              <span className="absolute -top-1 -right-1 bg-amber-400 text-slate-900 text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">{countCart}</span>
            )}
          </button>
          {!isStandalone && (
            <button
              onClick={instalarApp}
              className="flex items-center gap-1 bg-white text-pink-600 hover:bg-pink-50 rounded-full px-3 py-1.5 text-xs font-extrabold transition shadow"
              data-testid="btn-instalar-app-header"
              title="Instalar App no celular"
            >
              <Download className="h-4 w-4" /> <span className="hidden sm:inline">Instalar App</span><span className="sm:hidden">App</span>
            </button>
          )}
          {cliente ? (
            <>
              <button
                onClick={abrirPerfil}
                className="relative flex items-center gap-1 bg-white text-pink-600 hover:bg-pink-50 rounded-full px-3 py-1.5 text-xs font-extrabold transition shadow"
                data-testid="btn-meu-perfil"
                title="Meu Perfil"
              >
                <User className="h-4 w-4" /> <span className="hidden sm:inline">Meu Perfil</span><span className="sm:hidden">Perfil</span>
                {perfilData && (perfilData.em_analise.length + perfilData.em_andamento.length + perfilData.ativas.length) > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center animate-pulse">
                    {perfilData.em_analise.length + perfilData.em_andamento.length + perfilData.ativas.length}
                  </span>
                )}
              </button>
              <button onClick={handleLogout} className="text-white/70 hover:text-white text-xs inline-flex items-center gap-1" data-testid="btn-sair-loja">
                <LogOut className="h-3.5 w-3.5" /> Sair
              </button>
            </>
          ) : (
            <button
              onClick={() => { setAuthTab("login"); setShowAuthModal(true); }}
              className="flex items-center gap-1 bg-white text-pink-600 hover:bg-pink-50 rounded-full px-3 py-1.5 text-xs font-extrabold transition shadow"
              data-testid="btn-entrar-cadastrar"
              title="Entrar ou cadastrar"
            >
              <LogIn className="h-4 w-4" /> <span className="hidden sm:inline">Entrar / Cadastrar</span><span className="sm:hidden">Entrar</span>
            </button>
          )}
        </div>
      </header>

      {/* Banner Carousel */}
      {catalogo?.banners?.length > 0 && <BannerCarousel banners={catalogo.banners} speedMs={catalogo?.tema?.banner_speed_ms} />}

      {/* Card Situação da Cliente (destaque) */}
      {perfilData && (perfilData.ativas.length > 0 || perfilData.em_analise.length > 0 || perfilData.em_andamento.length > 0 || (perfilData.cliente?.cashback_saldo || 0) > 0) && (
        <div className="mx-3 mt-3 rounded-2xl border-2 border-pink-300 bg-gradient-to-br from-pink-50 via-white to-rose-50 shadow-sm" data-testid="card-situacao">
          <div className="p-3 grid grid-cols-2 gap-2">
            {perfilData.ativas.length > 0 && (() => {
              const nota = perfilData.ativas[0];
              const restante = Number(nota.valor_restante || 0);
              const dias = nota.dias_restantes;
              const corDias = dias < 0 ? 'text-red-700' : dias <= 3 ? 'text-amber-700' : 'text-gray-700';
              const labelDias = dias == null ? '' : dias < 0 ? `vencida há ${Math.abs(dias)}d` : dias === 0 ? 'vence hoje' : `faltam ${dias}d`;
              return (
                <div className="bg-white rounded-xl border border-red-200 p-2.5 col-span-2 flex items-center gap-2" data-testid="situacao-nota-aberta">
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] text-red-700 font-bold uppercase">Nota em aberto</p>
                    <p className="text-base font-extrabold text-red-700">{formatMoeda(restante)}</p>
                    <p className={`text-[10px] ${corDias}`}>{labelDias}</p>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); abrirPagarModal(nota); }}
                    disabled={!pagamentoDisponivel}
                    className={`shrink-0 font-extrabold text-xs rounded-lg px-3 py-2 shadow-md flex items-center gap-1 transition-colors text-white ${pagamentoDisponivel ? 'bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800' : 'bg-gray-400 cursor-not-allowed'}`}
                    data-testid="btn-pagar-home"
                    title={pagamentoDisponivel ? "Pagar agora" : "Pagamentos indisponíveis"}
                  >
                    💳 {pagamentoDisponivel ? 'PAGAR' : 'INDISP.'}
                  </button>
                </div>
              );
            })()}
            {perfilData.em_analise.length > 0 && (
              <div className="bg-white rounded-xl border border-amber-200 p-2.5" data-testid="situacao-em-analise">
                <p className="text-[10px] text-amber-700 font-bold uppercase">Em análise</p>
                <p className="text-base font-extrabold text-amber-700">{perfilData.em_analise.length} {perfilData.em_analise.length === 1 ? 'pedido' : 'pedidos'}</p>
                <p className="text-[10px] text-amber-600">aguardando aprovação</p>
              </div>
            )}
            {perfilData.em_andamento.length > 0 && (
              <div className="bg-white rounded-xl border border-emerald-200 p-2.5" data-testid="situacao-em-andamento">
                <p className="text-[10px] text-emerald-700 font-bold uppercase">Aprovado</p>
                <p className="text-base font-extrabold text-emerald-700">{perfilData.em_andamento.length}</p>
                <p className="text-[10px] text-emerald-600">aguardando entrega</p>
              </div>
            )}
            {(perfilData.cliente?.cashback_saldo || 0) > 0 && (
              <div className="bg-white rounded-xl border border-yellow-200 p-2.5" data-testid="situacao-cashback">
                <p className="text-[10px] text-amber-700 font-bold uppercase">Cashback</p>
                <p className="text-base font-extrabold text-amber-700">{formatMoeda(perfilData.cliente.cashback_saldo)}</p>
                <p className="text-[10px] text-amber-600">disponível</p>
              </div>
            )}
          </div>
          <button
            onClick={abrirPerfil}
            className="w-full bg-pink-600 hover:bg-pink-700 text-white font-bold py-2.5 text-sm rounded-b-2xl flex items-center justify-center gap-2 transition-colors"
            data-testid="btn-ver-perfil-card"
          >
            Ver Meu Perfil <span aria-hidden="true">→</span>
          </button>
        </div>
      )}

      {/* Busca */}
      <div className="max-w-5xl mx-auto px-4 py-3 space-y-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input placeholder="Buscar produtos..." value={busca} onChange={e => setBusca(e.target.value)} className="pl-10" data-testid="input-busca-produto" />
        </div>
        {catalogo?.categorias?.length > 0 && (
          <CategoriasFilter
            categorias={catalogo.categorias}
            ativa={categoriaFiltro}
            onSelecionar={(cat) => { setCategoriaFiltro(cat); if (cliente) { loadCatalogo(cliente, cat); } else { loadCatalogoPublico(cat); } }}
            corAtiva={corBtnComprar}
            layout={catalogo?.tema?.categorias_layout || "chips"}
          />
        )}
      </div>

      {/* Catálogo */}
      <main className="max-w-5xl mx-auto px-4">
        {loading && !catalogo && (
          <p className="text-center text-gray-400 py-16">Carregando catálogo...</p>
        )}
        {catalogo && produtosFiltrados.length === 0 && (
          <div className="text-center py-16 text-gray-400">
            <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p className="text-sm">{busca ? "Nenhum produto encontrado" : "Catálogo vazio no momento"}</p>
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {produtosFiltrados.map(p => {
            const k = keyOf(p);
            const qtd = cart[k] || 0;
            const preco = p.__isKit ? Number(p.preco || 0) : Number(p.preco_venda || p.preco || 0);
            return (
              <Card key={k} className={`overflow-hidden hover:shadow-lg transition-shadow ${p.__isKit ? 'ring-2 ring-amber-300' : ''}`} data-testid={`card-produto-${p.id}`}>
                <div className="aspect-square bg-gradient-to-br from-gray-100 to-gray-200 relative flex items-center justify-center">
                  {p.imagem_url ? (
                    <img src={p.imagem_url} alt={p.nome} className="w-full h-full object-cover" />
                  ) : (
                    <Package className="h-12 w-12 text-gray-300" />
                  )}
                  {p.__isKit && (
                    <span className="absolute top-2 left-2 bg-amber-500 text-white text-[10px] font-bold rounded px-2 py-0.5 shadow">KIT</span>
                  )}
                  {qtd > 0 && (
                    <span className="absolute top-2 right-2 bg-emerald-600 text-white text-xs font-bold rounded-full min-w-[24px] h-6 px-2 flex items-center justify-center">{qtd}</span>
                  )}
                </div>
                <CardContent className="p-3 space-y-1.5">
                  <p className="text-[10px] text-gray-400 uppercase">{p.__isKit ? (p.categoria || 'COMBO') : p.codigo_sku}</p>
                  <p className="text-xs font-semibold line-clamp-2 min-h-[2.5rem]">{p.nome}</p>
                  {(p.descricao_loja || p.descricao) && <p className="text-[10px] text-gray-500 line-clamp-2">{p.descricao_loja || p.descricao}</p>}
                  <p className="text-base font-bold text-emerald-700">{formatMoeda(preco)}</p>
                  <p className="text-[10px] text-gray-400">Disp.: {p.estoque}</p>
                  {qtd === 0 ? (
                    <Button size="sm" className={`w-full ${btnComprarClass} gap-1`} style={btnComprarStyle} onClick={() => addCart(p)} data-testid={`btn-add-${p.id}`}>
                      <Plus className="h-3.5 w-3.5" /> Adicionar
                    </Button>
                  ) : (
                    <div className="flex items-center justify-between gap-1">
                      <button onClick={() => subCart(k)} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center" data-testid={`btn-sub-${p.id}`}>
                        <Minus className="h-3.5 w-3.5" />
                      </button>
                      <span className="text-sm font-bold">{qtd}</span>
                      <button onClick={() => addCart(p)} className={`w-8 h-8 rounded-full ${btnComprarClass} text-white flex items-center justify-center`} style={btnComprarStyle} data-testid={`btn-inc-${p.id}`}>
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </main>

      {/* Barra inferior fixa (com carrinho) */}
      {countCart > 0 && !showCart && (
        <div className="fixed bottom-12 left-0 right-0 bg-white border-t shadow-lg px-4 py-3 z-20">
          <div className="max-w-5xl mx-auto flex items-center justify-between gap-3">
            <div>
              <p className="text-[10px] text-gray-500 uppercase">{countCart} {countCart === 1 ? "item" : "itens"}</p>
              <p className="text-lg font-bold text-emerald-700">{formatMoeda(totalCart)}</p>
            </div>
            <Button onClick={() => setShowCart(true)} className={`${btnComprarClass} gap-2`} style={btnComprarStyle} data-testid="btn-ver-carrinho">
              <ShoppingCart className="h-4 w-4" /> Ver Carrinho
            </Button>
          </div>
        </div>
      )}

      {/* Modal instruções iOS */}
      {showIOSInstall && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/60" onClick={() => setShowIOSInstall(false)} data-testid="modal-ios-install">
          <div className="bg-white rounded-2xl max-w-sm w-full p-5 space-y-4 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <h2 className="text-lg font-bold text-pink-700">Instalar App no iPhone</h2>
              <button onClick={() => setShowIOSInstall(false)} className="text-gray-400 hover:text-gray-700 text-2xl leading-none" data-testid="fechar-ios">×</button>
            </div>
            <p className="text-xs text-gray-500">O Safari do iPhone precisa de 2 passos rápidos:</p>
            <ol className="space-y-3 text-sm">
              <li className="flex items-start gap-3 bg-pink-50 rounded-lg p-3">
                <span className="bg-pink-500 text-white font-bold rounded-full w-7 h-7 flex items-center justify-center shrink-0">1</span>
                <div>
                  Toque no botão <strong>Compartilhar</strong>
                  <div className="inline-block ml-1.5 align-middle bg-white border border-gray-300 rounded px-1.5 py-0.5 text-xs">
                    <span aria-hidden="true" className="font-bold">⬆</span>
                  </div>
                  <p className="text-[11px] text-gray-600 mt-1">Fica na barra inferior do Safari (um quadrado com seta para cima).</p>
                </div>
              </li>
              <li className="flex items-start gap-3 bg-pink-50 rounded-lg p-3">
                <span className="bg-pink-500 text-white font-bold rounded-full w-7 h-7 flex items-center justify-center shrink-0">2</span>
                <div>
                  Role e escolha <strong>"Adicionar à Tela de Início"</strong>
                  <p className="text-[11px] text-gray-600 mt-1">Depois toque em <em>Adicionar</em> no canto superior direito.</p>
                </div>
              </li>
            </ol>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2 text-xs text-emerald-800">
              ✅ Pronto! O app <strong>DAL COTONE MS</strong> aparecerá na tela inicial como um aplicativo normal.
            </div>
            <Button onClick={() => setShowIOSInstall(false)} className="w-full bg-pink-600 hover:bg-pink-700" data-testid="btn-entendi-ios">Entendi</Button>
          </div>
        </div>
      )}

      {/* Drawer Meu Perfil */}
      {showPerfil && (
        <div className="fixed inset-0 z-40 flex" onClick={() => setShowPerfil(false)} data-testid="drawer-perfil">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div className="relative ml-auto w-full max-w-md bg-white shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-pink-400 to-rose-400 text-white">
              <h2 className="text-lg font-bold flex items-center gap-2"><User className="h-5 w-5" /> Meu Perfil</h2>
              <button onClick={() => setShowPerfil(false)} className="text-white/80 hover:text-white text-2xl leading-none">×</button>
            </div>
            <Button
              onClick={() => setShowPerfil(false)}
              className="mx-4 mt-3 bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-700 hover:to-rose-700 text-white font-extrabold text-base py-6 rounded-xl shadow-lg flex items-center justify-center gap-2"
              data-testid="btn-fazer-novo-pedido"
            >
              <ShoppingBag className="h-5 w-5" /> FAZER NOVO PEDIDO
            </Button>
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {loadingPerfil && <p className="text-center text-gray-400 py-8 text-sm">Carregando...</p>}
              {!loadingPerfil && perfilData && (
                <>
                  {/* Cashback info */}
                  <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3 space-y-1" data-testid="perfil-cashback">
                    <p className="text-[11px] font-semibold text-amber-700 uppercase">Meu Cashback</p>
                    <p className="text-2xl font-bold text-amber-800">{formatMoeda(perfilData.cliente.cashback_saldo)}</p>
                    {perfilData.cliente.cashback_proxima_expiracao && (
                      <p className="text-[11px] text-amber-700">
                        ⏱ Próxima expiração: {new Date(perfilData.cliente.cashback_proxima_expiracao).toLocaleDateString("pt-BR")} (validade de 60 dias corridos)
                      </p>
                    )}
                    {perfilData.cliente.cashback_saldo === 0 && (
                      <p className="text-[11px] text-amber-700">Faça compras para acumular cashback no seu Nível {NIVEIS[perfilData.cliente.nivel]?.nome}.</p>
                    )}
                  </div>

                  {/* Em análise */}
                  {perfilData.em_analise.length > 0 && (
                    <Section title="Pedidos em análise" icon={<Clock className="h-4 w-4 text-amber-600" />} testid="sec-em-analise">
                      {perfilData.em_analise.map(p => <PedidoAnaliseCard key={p.id} p={p} />)}
                    </Section>
                  )}

                  {/* Aprovados em andamento */}
                  {perfilData.em_andamento.length > 0 && (
                    <Section title="Pedidos aprovados (aguardando entrega)" icon={<CircleCheck className="h-4 w-4 text-green-600" />} testid="sec-em-andamento">
                      {perfilData.em_andamento.map(p => <PedidoAprovadoCard key={p.id} p={p} />)}
                    </Section>
                  )}

                  {/* Ativas com dívida */}
                  {perfilData.ativas.length > 0 && (
                    <Section title="Notas em aberto" icon={<FileText className="h-4 w-4 text-blue-600" />} testid="sec-ativas">
                      {perfilData.ativas.map(p => <NotaAtivaCard key={p.id} p={p} cliente={cliente} pagamentoDisponivel={pagamentoDisponivel} onPagar={() => abrirPagarModal(p)} />)}
                    </Section>
                  )}

                  {/* Quitadas */}
                  {perfilData.quitadas.length > 0 && (
                    <Section title="Notas quitadas" icon={<Check className="h-4 w-4 text-emerald-600" />} testid="sec-quitadas">
                      {perfilData.quitadas.slice(0, 10).map(p => <NotaQuitadaCard key={p.id} p={p} />)}
                    </Section>
                  )}

                  {/* Canceladas */}
                  {perfilData.canceladas && perfilData.canceladas.length > 0 && (
                    <Section title="Pedidos cancelados" icon={<Trash2 className="h-4 w-4 text-red-600" />} testid="sec-canceladas">
                      {perfilData.canceladas.slice(0, 10).map(p => <PedidoCanceladoCard key={p.id} p={p} />)}
                    </Section>
                  )}

                  {perfilData.em_analise.length === 0 && perfilData.em_andamento.length === 0 && perfilData.ativas.length === 0 && perfilData.quitadas.length === 0 && (!perfilData.canceladas || perfilData.canceladas.length === 0) && (
                    <p className="text-center text-gray-400 py-8 text-sm">Você ainda não tem pedidos.</p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Pagar Promissória (InfinitePay) */}
      {pagarPromissoria && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60" onClick={fecharPagarModal} data-testid="modal-pagar-promissoria">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-emerald-500 to-green-600 text-white px-5 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-extrabold">Pagar Nota #{pagarPromissoria.numero_pedido}</h2>
                <p className="text-[11px] opacity-90">Pague via PIX, Crédito ou Débito</p>
              </div>
              <button onClick={fecharPagarModal} className="text-white/80 hover:text-white text-2xl leading-none" data-testid="btn-fechar-pagar-modal">×</button>
            </div>
            <div className="p-5 space-y-4">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 grid grid-cols-3 gap-2 text-xs">
                <div>
                  <p className="text-gray-500">Total</p>
                  <p className="font-bold">{formatMoeda(pagarPromissoria.valor_total)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Pago</p>
                  <p className="font-bold text-emerald-700">{formatMoeda(pagarPromissoria.valor_pago)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Restante</p>
                  <p className="font-extrabold text-red-600" data-testid="txt-saldo-restante">{formatMoeda(pagarPromissoria.valor_restante)}</p>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-700 mb-1 block">Valor que deseja pagar (R$)</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={Number(pagarPromissoria.valor_restante)}
                  value={pagarValor}
                  onChange={(e) => setPagarValor(e.target.value)}
                  className="text-lg font-bold"
                  data-testid="input-valor-pagar"
                  autoFocus
                />
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={() => setPagarValor(Number(pagarPromissoria.valor_restante).toFixed(2))}
                    className="text-[11px] bg-emerald-100 text-emerald-800 hover:bg-emerald-200 rounded-full px-3 py-1 font-bold"
                    data-testid="btn-quitar-tudo"
                  >
                    Pagar tudo ({formatMoeda(pagarPromissoria.valor_restante)})
                  </button>
                  <button
                    onClick={() => setPagarValor((Number(pagarPromissoria.valor_restante) / 2).toFixed(2))}
                    className="text-[11px] bg-amber-100 text-amber-800 hover:bg-amber-200 rounded-full px-3 py-1 font-bold"
                    data-testid="btn-pagar-metade"
                  >
                    50%
                  </button>
                </div>
              </div>

              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs">
                <p className="font-bold text-emerald-800 mb-1">💳 Métodos aceitos:</p>
                <p className="text-emerald-700">PIX · Cartão de Crédito · Cartão de Débito</p>
                <p className="text-[10px] text-emerald-600 mt-1">Pagamento processado de forma segura pela InfinitePay.</p>
              </div>

              <Button
                onClick={confirmarPagar}
                disabled={pagarLoading || !pagarValor || parseFloat(pagarValor) <= 0}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold py-6 text-base gap-2"
                data-testid="btn-confirmar-pagar"
              >
                {pagarLoading ? "Gerando link..." : "🔒 Ir para o Pagamento"}
              </Button>

              {/* Fallback: se o redirect automático falhar (iOS Safari bloqueia em alguns casos),
                  mostra um <a> nativo que SEMPRE funciona com um toque direto do usuário. */}
              {checkoutUrlFallback && (
                <a
                  href={checkoutUrlFallback}
                  className="block w-full bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-extrabold py-4 text-center rounded-md text-base"
                  data-testid="link-pagar-fallback"
                  rel="noopener"
                >
                  ✅ Toque aqui para abrir o pagamento
                </a>
              )}

              <p className="text-[10px] text-gray-500 text-center">Você será redirecionado para o InfinitePay nesta mesma janela.</p>
            </div>
          </div>
        </div>
      )}

      {/* Modal central Login / Cadastro (aparece ao finalizar sem login) */}
      {showAuthModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={() => { setShowAuthModal(false); resetCadastroForm(); }} data-testid="modal-auth-loja">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-pink-500 to-rose-500 text-white px-5 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold">Entrar / Cadastrar-se</h2>
                <p className="text-[11px] opacity-90">Para finalizar seu pedido</p>
              </div>
              <button onClick={() => { setShowAuthModal(false); resetCadastroForm(); }} className="text-white/80 hover:text-white text-2xl leading-none" data-testid="btn-fechar-auth-modal">×</button>
            </div>

            {cadastroEnviado ? (
              <div className="p-6 text-center space-y-3" data-testid="cadastro-enviado-msg">
                <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
                  <Check className="h-8 w-8" />
                </div>
                <h3 className="text-lg font-bold text-emerald-700">Cadastro enviado!</h3>
                <p className="text-sm text-gray-600">
                  Recebemos seus dados. Um vendedor irá <strong>validar seu cadastro</strong> e definir sua rota de entrega.
                </p>
                <p className="text-xs text-gray-500">Em breve você receberá uma confirmação. Após a aprovação, basta entrar com seu telefone para finalizar pedidos.</p>
                <Button onClick={() => { setShowAuthModal(false); resetCadastroForm(); }} className="w-full bg-pink-500 hover:bg-pink-600 mt-2" data-testid="btn-cadastro-ok">
                  Continuar navegando
                </Button>
              </div>
            ) : (
              <>
                <div className="flex border-b">
                  <button
                    onClick={() => setAuthTab("login")}
                    className={`flex-1 py-3 text-sm font-bold transition flex items-center justify-center gap-1.5 ${authTab === "login" ? "text-pink-600 border-b-2 border-pink-500 bg-pink-50/50" : "text-gray-500 hover:bg-gray-50"}`}
                    data-testid="tab-login"
                  >
                    <LogIn className="h-4 w-4" /> Já sou cliente
                  </button>
                  <button
                    onClick={() => setAuthTab("cadastro")}
                    className={`flex-1 py-3 text-sm font-bold transition flex items-center justify-center gap-1.5 ${authTab === "cadastro" ? "text-pink-600 border-b-2 border-pink-500 bg-pink-50/50" : "text-gray-500 hover:bg-gray-50"}`}
                    data-testid="tab-cadastro"
                  >
                    <UserPlus className="h-4 w-4" /> Quero me cadastrar
                  </button>
                </div>

                {authTab === "login" ? (
                  <form onSubmit={handleLogin} className="p-5 space-y-4" data-testid="form-login-modal">
                    <div>
                      <label className="text-xs font-semibold text-gray-700 mb-1 block">Seu telefone cadastrado</label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          type="tel"
                          inputMode="numeric"
                          placeholder="(67) 99999-9999"
                          value={telefone}
                          onChange={e => setTelefone(e.target.value)}
                          className="pl-10"
                          autoFocus
                          data-testid="input-telefone-modal"
                        />
                      </div>
                      <p className="text-[11px] text-gray-400 mt-1">Use o mesmo número usado no seu cadastro.</p>
                    </div>
                    <Button type="submit" disabled={loading} className="w-full bg-pink-500 hover:bg-pink-600 gap-2" data-testid="btn-entrar-modal">
                      {loading ? "Validando..." : "Entrar"}
                    </Button>
                    <p className="text-center text-xs text-gray-500">
                      Não tem cadastro?{" "}
                      <button type="button" onClick={() => setAuthTab("cadastro")} className="text-pink-600 hover:underline font-bold">Cadastre-se aqui</button>
                    </p>
                  </form>
                ) : (
                  <form onSubmit={handleCadastro} className="p-5 space-y-3 max-h-[70vh] overflow-y-auto" data-testid="form-cadastro-modal">
                    <p className="text-xs text-gray-500 bg-amber-50 border border-amber-200 rounded p-2">
                      <strong>Atenção:</strong> seu cadastro passará por aprovação do vendedor antes de poder finalizar pedidos.
                    </p>
                    <div>
                      <label className="text-xs font-semibold text-gray-700 mb-1 block">Nome completo *</label>
                      <Input
                        value={cadastroForm.nome_completo}
                        onChange={e => setCadastroForm({ ...cadastroForm, nome_completo: e.target.value })}
                        placeholder="Seu nome completo"
                        required
                        data-testid="input-cad-nome"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-700 mb-1 block">Telefone / WhatsApp *</label>
                      <Input
                        type="tel"
                        inputMode="numeric"
                        value={cadastroForm.telefone}
                        onChange={e => setCadastroForm({ ...cadastroForm, telefone: e.target.value })}
                        placeholder="(67) 99999-9999"
                        required
                        data-testid="input-cad-telefone"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-700 mb-1 block">CPF / RG (opcional)</label>
                      <Input
                        value={cadastroForm.cpf_rg}
                        onChange={e => setCadastroForm({ ...cadastroForm, cpf_rg: e.target.value })}
                        placeholder="000.000.000-00"
                        data-testid="input-cad-cpf"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-700 mb-1 block">Endereço (opcional)</label>
                      <Input
                        value={cadastroForm.endereco}
                        onChange={e => setCadastroForm({ ...cadastroForm, endereco: e.target.value })}
                        placeholder="Rua, número, bairro"
                        data-testid="input-cad-endereco"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-700 mb-1 block">Cidade (opcional)</label>
                      <Input
                        value={cadastroForm.cidade}
                        onChange={e => setCadastroForm({ ...cadastroForm, cidade: e.target.value })}
                        placeholder="Ex: Dourados - MS"
                        data-testid="input-cad-cidade"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs font-semibold text-gray-700 mb-1 block">Nascimento (opcional)</label>
                        <Input
                          type="date"
                          value={cadastroForm.data_nascimento}
                          onChange={e => setCadastroForm({ ...cadastroForm, data_nascimento: e.target.value })}
                          data-testid="input-cad-nascimento"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-gray-700 mb-1 block">Instagram (opcional)</label>
                        <Input
                          value={cadastroForm.instagram}
                          onChange={e => setCadastroForm({ ...cadastroForm, instagram: e.target.value })}
                          placeholder="@usuario"
                          data-testid="input-cad-instagram"
                        />
                      </div>
                    </div>
                    <Button type="submit" disabled={cadastroLoading} className="w-full bg-pink-500 hover:bg-pink-600 gap-2" data-testid="btn-enviar-cadastro">
                      {cadastroLoading ? "Enviando..." : "Enviar Cadastro"}
                    </Button>
                    <p className="text-center text-xs text-gray-500">
                      Já tem cadastro?{" "}
                      <button type="button" onClick={() => setAuthTab("login")} className="text-pink-600 hover:underline font-bold">Faça login</button>
                    </p>
                  </form>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Drawer Carrinho */}      {showCart && (
        <div className="fixed inset-0 z-40 flex" onClick={() => setShowCart(false)}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div className="relative ml-auto w-full max-w-md bg-white shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h2 className="text-lg font-bold">Meu Carrinho ({countCart})</h2>
              <button onClick={() => setShowCart(false)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {Object.keys(cart).length === 0 && <p className="text-center text-gray-400 py-8 text-sm">Carrinho vazio</p>}
              {Object.entries(cart).map(([k, qtd]) => {
                const item = findItemByKey(k);
                if (!item) return null;
                const preco = k.startsWith("kit:") ? Number(item.preco || 0) : Number(item.preco_venda || item.preco || 0);
                return (
                  <div key={k} className="flex items-center gap-2 p-2 rounded-lg border" data-testid={`cart-item-${item.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold truncate">{item.nome}{k.startsWith("kit:") && <span className="ml-1 text-[9px] bg-amber-500 text-white px-1 rounded">KIT</span>}</p>
                      <p className="text-[10px] text-gray-500">{formatMoeda(preco)} cada</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => subCart(k)} className="w-6 h-6 rounded bg-gray-100 hover:bg-gray-200 flex items-center justify-center">
                        <Minus className="h-3 w-3" />
                      </button>
                      <span className="text-sm font-bold w-6 text-center">{qtd}</span>
                      <button onClick={() => incCartByKey(k)} className={`w-6 h-6 rounded ${btnComprarClass} text-white flex items-center justify-center`} style={btnComprarStyle} data-testid={`cart-plus-${item.id}`}>
                        <Plus className="h-3 w-3" />
                      </button>
                    </div>
                    <p className="text-sm font-bold w-20 text-right">{formatMoeda(preco * qtd)}</p>
                    <button onClick={() => removerCart(k)} className="p-1 text-red-500 hover:bg-red-50 rounded">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
            <div className="border-t p-4 pb-20 space-y-3 bg-gray-50">
              <div>
                <label className="text-xs font-semibold text-gray-700">Observações (opcional)</label>
                <textarea value={observacoes} onChange={e => setObservacoes(e.target.value)} rows={2} className="w-full text-sm border rounded p-2 mt-1" placeholder="Ex: Pode entregar na manhã" data-testid="textarea-obs-pedido" />
              </div>

              {/* Mensagem de cashback ganho */}
              {cliente && cashbackPrevisto > 0 && cliente.nivel && NIVEIS[cliente.nivel] && (
                <div className="rounded-lg bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-300 px-3 py-2 flex items-start gap-2" data-testid="msg-cashback-ganho">
                  <span className="text-xl leading-none">{NIVEIS[cliente.nivel].emoji}</span>
                  <div className="flex-1 text-xs">
                    <p className="font-bold text-amber-900">Com essa compra você ganha <span className="text-emerald-700">{formatMoeda(cashbackPrevisto)}</span> em Cashback</p>
                    <p className="text-amber-700">Garantido pelo Nível {NIVEIS[cliente.nivel].nome} ({pctCashbackNivel}%)</p>
                    <p className="text-[10px] text-amber-600 mt-0.5">⏱ Cashback válido por 60 dias corridos após a aprovação.</p>
                  </div>
                </div>
              )}

              {/* Usar cashback acumulado */}
              {saldoCashback > 0 && (
                <label className="flex items-center gap-2 text-xs bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 cursor-pointer" data-testid="usar-cashback-wrapper">
                  <input
                    type="checkbox"
                    checked={usarCashback}
                    onChange={e => setUsarCashback(e.target.checked)}
                    className="w-4 h-4 accent-emerald-600"
                    data-testid="checkbox-usar-cashback"
                  />
                  <span className="flex-1">
                    <span className="font-bold text-emerald-800">Usar meu cashback</span>
                    <span className="text-emerald-700"> ({formatMoeda(saldoCashback)} disponível)</span>
                  </span>
                </label>
              )}

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Total</span>
                <span className={`text-xl font-bold ${cashbackAplicado > 0 ? 'text-gray-400 line-through' : 'text-emerald-700'}`}>{formatMoeda(totalCart)}</span>
              </div>
              {cashbackAplicado > 0 && (
                <>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-emerald-700">− Cashback aplicado</span>
                    <span className="text-emerald-700 font-semibold">−{formatMoeda(cashbackAplicado)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700 font-semibold">A pagar</span>
                    <span className="text-2xl font-bold text-emerald-700" data-testid="total-a-pagar">{formatMoeda(totalAPagar)}</span>
                  </div>
                </>
              )}
              {cliente && cliente.limite_compra > 0 && (
                <p className="text-[11px] text-gray-500">Seu limite de compra: {formatMoeda(cliente.limite_compra)}</p>
              )}
              <Button onClick={finalizar} disabled={enviando || countCart === 0} className={`w-full ${btnComprarClass} gap-2`} style={btnComprarStyle} data-testid="btn-finalizar-pedido">
                {enviando ? "Enviando..." : (cliente ? "Finalizar Pedido" : "Fazer Login / Cadastrar-se")}
              </Button>
              <p className="text-[10px] text-gray-400 text-center">{cliente ? "Seu pedido ficará aguardando aprovação do vendedor." : "Para finalizar, é preciso entrar com seu telefone ou criar um cadastro."}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Filtro de categorias com múltiplos layouts (chips / grade2 / grade3 / dropdown / expansivel)
function CategoriasFilter({ categorias, ativa, onSelecionar, corAtiva, layout }) {
  const [expandido, setExpandido] = useState(false);
  const todas = [{ id: "", label: "Todas" }, ...categorias.map(c => ({ id: c, label: c }))];

  const baseActive = corAtiva ? { backgroundColor: corAtiva } : undefined;
  const classActive = corAtiva ? "text-white" : "bg-emerald-600 text-white";
  const classIdle = "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50";

  if (layout === "dropdown") {
    return (
      <div className="relative" data-testid="categorias-dropdown">
        <select
          value={ativa || ""}
          onChange={e => onSelecionar(e.target.value || null)}
          className="w-full text-sm font-medium px-3 py-2 rounded-lg border border-gray-200 bg-white appearance-none pr-9 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-emerald-400"
          data-testid="select-categoria"
        >
          {todas.map(c => (
            <option key={c.id} value={c.id}>{c.label}</option>
          ))}
        </select>
        <svg className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd"/></svg>
      </div>
    );
  }

  if (layout === "grade2" || layout === "grade3") {
    const cols = layout === "grade3" ? "grid-cols-3" : "grid-cols-2";
    return (
      <div className={`grid ${cols} gap-2`} data-testid={`categorias-${layout}`}>
        {todas.map(c => {
          const isActive = (c.id === "" && !ativa) || c.id === ativa;
          return (
            <button
              key={c.id || "todas"}
              onClick={() => onSelecionar(c.id || null)}
              className={`text-xs font-medium px-2 py-2 rounded-lg transition truncate ${isActive ? classActive : classIdle}`}
              style={isActive ? baseActive : undefined}
              data-testid={c.id ? `grid-cat-${c.id}` : "grid-todas"}
            >
              {c.label}
            </button>
          );
        })}
      </div>
    );
  }

  if (layout === "expansivel") {
    const rotuloAtual = ativa || "Todas";
    const visiveis = expandido ? todas : [];
    return (
      <div className="space-y-1" data-testid="categorias-expansivel">
        <button
          onClick={() => setExpandido(e => !e)}
          className="w-full flex items-center justify-between text-sm font-medium px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50"
          data-testid="btn-toggle-categorias"
        >
          <span>Categoria: <strong>{rotuloAtual}</strong></span>
          <svg className={`h-4 w-4 text-gray-500 transition-transform ${expandido ? "rotate-180" : ""}`} viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd"/></svg>
        </button>
        {expandido && (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden divide-y" data-testid="lista-categorias-expandida">
            {visiveis.map(c => {
              const isActive = (c.id === "" && !ativa) || c.id === ativa;
              return (
                <button
                  key={c.id || "todas"}
                  onClick={() => { onSelecionar(c.id || null); setExpandido(false); }}
                  className={`w-full text-left text-sm px-3 py-2 transition ${isActive ? classActive : "hover:bg-gray-50 text-gray-700"}`}
                  style={isActive ? baseActive : undefined}
                  data-testid={c.id ? `exp-cat-${c.id}` : "exp-todas"}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Default: chips horizontais (comportamento antigo)
  return (
    <div className="flex gap-2 overflow-x-auto pb-1" data-testid="categorias-chips">
      {todas.map(c => {
        const isActive = (c.id === "" && !ativa) || c.id === ativa;
        return (
          <button
            key={c.id || "todas"}
            onClick={() => onSelecionar(c.id || null)}
            className={`shrink-0 text-xs px-3 py-1 rounded-full font-medium transition ${isActive ? classActive : classIdle}`}
            style={isActive ? baseActive : undefined}
            data-testid={c.id ? `chip-cat-${c.id}` : "chip-todas"}
          >
            {c.label}
          </button>
        );
      })}
    </div>
  );
}

// Carrossel simples de banners (auto-play configurável, navegação por dots)
function BannerCarousel({ banners, speedMs }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (banners.length <= 1) return;
    const interval = Math.max(1000, Number(speedMs) || 3333);
    const t = setInterval(() => setIdx(i => (i + 1) % banners.length), interval);
    return () => clearInterval(t);
  }, [banners.length, speedMs]);
  if (!banners?.length) return null;
  return (
    <div className="max-w-5xl mx-auto px-4 pt-3">
      <div className="relative rounded-xl overflow-hidden shadow-md" data-testid="banner-carousel">
        <img src={banners[idx].url} alt={`Banner ${idx + 1}`} className="w-full h-40 md:h-56 object-cover transition-opacity duration-500" />
        {banners.length > 1 && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
            {banners.map((_, i) => (
              <button key={i} onClick={() => setIdx(i)} className={`w-2 h-2 rounded-full transition-all ${i === idx ? 'bg-white w-4' : 'bg-white/50'}`} aria-label={`Banner ${i+1}`} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


// ====== Componentes do "Meu Perfil" ======

function Section({ title, icon, children, testid }) {
  return (
    <div data-testid={testid}>
      <h3 className="text-xs font-bold uppercase text-gray-500 mb-2 flex items-center gap-1.5">{icon} {title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function PedidoAnaliseCard({ p }) {
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs" data-testid={`pedido-analise-${p.id}`}>
      <div className="flex justify-between items-center mb-1">
        <span className="font-bold">Pedido #{p.numero_pedido}</span>
        <span className="text-[10px] bg-amber-200 text-amber-900 rounded-full px-2 py-0.5 font-bold">EM ANÁLISE</span>
      </div>
      <p className="text-emerald-700 font-bold text-base">{formatMoeda(p.valor_total)}</p>
      <p className="text-[10px] text-gray-500">Itens: {(p.itens || []).length} · Criado: {p.data_compra?.split('-').reverse().join('/')}</p>
    </div>
  );
}

function PedidoAprovadoCard({ p }) {
  const etapa = p.loja_fluxo === "aguardando_whatsapp"
    ? { label: "Aprovado — aguardando contato do vendedor", cls: "bg-green-200 text-green-900" }
    : { label: "Aguardando entrega", cls: "bg-red-200 text-red-900" };
  return (
    <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-xs" data-testid={`pedido-aprovado-${p.id}`}>
      <div className="flex justify-between items-center mb-1">
        <span className="font-bold">Pedido #{p.numero_pedido}</span>
        <span className={`text-[10px] rounded-full px-2 py-0.5 font-bold ${etapa.cls}`}>{etapa.label.toUpperCase()}</span>
      </div>
      <p className="text-emerald-700 font-bold text-base">{formatMoeda(p.valor_total)}</p>
      <p className="text-[10px] text-gray-500">Vencimento: {p.data_vencimento?.split('-').reverse().join('/')}</p>
    </div>
  );
}

function NotaAtivaCard({ p, cliente, onPagar, pagamentoDisponivel = true }) {
  const diasRest = p.dias_restantes;
  const restColor = diasRest < 0 ? "text-red-600" : diasRest <= 3 ? "text-amber-600" : "text-gray-700";
  const restLabel = diasRest == null ? "" : diasRest < 0 ? `VENCIDA há ${Math.abs(diasRest)}d` : diasRest === 0 ? "Vence HOJE" : `Faltam ${diasRest}d`;
  return (
    <div className="bg-white border border-blue-200 rounded-lg p-3 text-xs space-y-1" data-testid={`nota-ativa-${p.id}`}>
      <div className="flex justify-between items-center">
        <span className="font-bold">Nota #{p.numero_pedido}</span>
        <span className={`text-[10px] font-bold ${restColor}`}>{restLabel}</span>
      </div>
      <div className="grid grid-cols-2 gap-1">
        <div><span className="text-gray-500">Total:</span> <span className="font-semibold">{formatMoeda(p.valor_total)}</span></div>
        <div><span className="text-gray-500">Pago:</span> <span className="font-semibold text-emerald-700">{formatMoeda(p.valor_pago)}</span></div>
        <div className="col-span-2"><span className="text-gray-500">Restante:</span> <span className="font-bold text-red-600">{formatMoeda(p.valor_restante)}</span></div>
      </div>
      {p.pagamentos && p.pagamentos.length > 0 && (
        <div className="mt-1 pt-1 border-t text-[10px]" data-testid={`historico-pagamentos-${p.id}`}>
          <p className="font-semibold text-gray-600 mb-0.5">Histórico de pagamentos:</p>
          {p.pagamentos.map((pg, i) => {
            const data = (pg.data_pagamento || "").includes("T")
              ? pg.data_pagamento.split("T")[0].split("-").reverse().join("/")
              : (pg.data_pagamento || "").split("-").reverse().join("/");
            return (
              <div key={i} className="flex justify-between" data-testid={`hist-pag-${p.id}-${i}`}>
                <span>
                  {pg.via_infinitepay && <span className="text-[9px] bg-emerald-500 text-white rounded px-1 mr-1">PIX/CARD</span>}
                  {pg.forma_pagamento} · {data}
                </span>
                <span className="text-emerald-700 font-semibold">{formatMoeda(pg.valor)}</span>
              </div>
            );
          })}
        </div>
      )}
      {onPagar && cliente && (
        pagamentoDisponivel ? (
          <button
            onClick={onPagar}
            className="w-full mt-2 bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-sm rounded-lg py-2.5 shadow-md flex items-center justify-center gap-1.5 transition-colors animate-pulse-slow"
            data-testid={`btn-pagar-${p.id}`}
          >
            💳 PAGAR — {formatMoeda(p.valor_restante)}
          </button>
        ) : (
          <div className="w-full mt-2 bg-gray-100 border border-gray-200 text-gray-600 text-xs rounded-lg py-2 flex items-center justify-center gap-1.5" data-testid={`pagamentos-indisponiveis-${p.id}`}>
            ⚠️ Pagamentos indisponíveis no momento
          </div>
        )
      )}
    </div>
  );
}

function NotaQuitadaCard({ p }) {
  return (
    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2 text-xs flex justify-between items-center" data-testid={`nota-quitada-${p.id}`}>
      <div>
        <span className="font-bold">Nota #{p.numero_pedido}</span>
        <span className="text-gray-500 ml-2">{p.data_compra?.split('-').reverse().join('/')}</span>
      </div>
      <span className="text-emerald-700 font-bold">{formatMoeda(p.valor_total)}</span>
    </div>
  );
}

function PedidoCanceladoCard({ p }) {
  const fmtBr = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return ""; }
  };
  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs" data-testid={`pedido-cancelado-${p.id}`}>
      <div className="flex justify-between items-center mb-1">
        <span className="font-bold line-through text-red-700">Pedido #{p.numero_pedido}</span>
        <span className="text-[10px] bg-red-200 text-red-900 rounded-full px-2 py-0.5 font-bold">CANCELADO</span>
      </div>
      <p className="text-gray-600"><span className="font-semibold">{formatMoeda(p.valor_total)}</span></p>
      <p className="text-[10px] text-gray-500">
        Itens: {(p.itens || []).length}
        {p.cancelado_em && <> · Cancelado em {fmtBr(p.cancelado_em)}</>}
      </p>
    </div>
  );
}
